/**
 * Created by rathompson on 12/29/21
 * File: PanoramaCard
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useEffect, useMemo, useRef, useState, useContext, useCallback } from 'react'
import { Title, Body } from '@panwds/react-ui'
import Link from '../../../common/components/link/Link'
import { Input, Select } from '@panwds/react-form'
import styled from 'styled-components'
import { useFormContext } from 'react-hook-form'
import { APP_ID_LOGGING_SERVICE, APP_ID_PANORAMA, APP_ID_PRISMA_ACCESS_PANORAMA } from '../../../common/constants/GlobalConstants'
import { ADD_NEW, CREATE_NEW } from '../constants/SubscriptionConstant'
import { ERROR_PANORAMA_INVALID } from '../../constants/LabelConstants'
import { SELECT_PANORAMA } from '../constants/SubscriptionConstant'
import { ARRAY, getSignature } from '../../../common/utils/Utils'
import { LABEL_PANORAMA } from '../../../common/constants/LabelConstants'
import AuthContext from '../../../common/context/AuthContext'
import GlobalContext from '../../../common/context/GlobalContext'
import {
  LoadCSPDevices,
  LoadDevices,
} from '../../../common/actions/GlobalActions'

/**
 To set up the Panorama virtual appliance:
 1. Review the prerequisites.
 2. Install Panorama.
 3. Perform initial configuration.
 4. Upgrade your Panorama.  Refer to the compatibility matrix for the minimum Panorama version.x
 */

/**
 To set up a Panorama hardware appliance:
 1. Complete steps 1 through 6 to set up the Panorama hardware appliance.
 2. Refer to the compatibility matrix for the minimum Panorama version.
 */

const Details = styled.div`
  button {
    justify-content: space-between;
    color: #333333;
  }
`

const PanoramaCard = ({ loading, isCloudManaged, licenses = [], setError: _setError, selectedTSGInstances }) => {

  const { setValue, trigger, reset, setError, getValues, watch, clearErrors } = useFormContext()

  const { auth } = useContext(AuthContext)
  const { dispatch } = useContext(GlobalContext)

  const currentValuesRef = useRef({})
  const watchSupportAccount = watch('support_account')
  const watchTsg = watch('tsg')

  currentValuesRef.current.support_account_id = watchSupportAccount?.id
  currentValuesRef.current.tsg_id = watchTsg?.id

  const [isSNLocked, setSNLock] = useState(true)
  const [isDropdownLocked, setDropdownLock] = useState(true)
  const [lockTSGPanorama, setLockTSGPanorama] = useState(false)
  const [selectedPanorama, setSelectedPanorama] = useState({})
  const [serialNumber, setSerialNumber] = useState(undefined)
  const [panoramaItems, setPanoramaItems] = useState([])
  const [availablePanoramaDevices, setAvailablePanorama] = useState([])
  const [panoramaLoading, setPanoramaLoading] = useState(false)
  const [panoramaDeviceMap, setPanoramaMap] = useState({})

  const panoramaLicenses = useMemo(() => licenses.filter(license => license.app_id === APP_ID_PANORAMA), [licenses])

  const loadPanorama = useCallback(async (tsg_id, support_account_id) => {
    let _panoramaDevices = []
    if (panoramaDeviceMap[support_account_id]) {
      _panoramaDevices = panoramaDeviceMap[support_account_id]
    }
    else {
      const signature = getSignature(support_account_id, auth?.user?.supportAccountIds)
      const notonorafter = auth?.user?.notonorafter
      const xsig = `${support_account_id} ${notonorafter} ${signature}`
      const devices = await LoadCSPDevices(support_account_id, xsig, dispatch)
      if (currentValuesRef.current.support_account_id === support_account_id && currentValuesRef.current.tsg_id === tsg_id) {
        _panoramaDevices = devices.filter(each => each.app_id === APP_ID_PANORAMA && !each.tsg_id)
        setPanoramaMap((prevState) => ({
          ...prevState,
          [support_account_id]: _panoramaDevices,
        }))
      }
    }
    const tsgCDL = selectedTSGInstances.get(APP_ID_LOGGING_SERVICE)
    let panoramaDevices = []
    if (tsgCDL && _panoramaDevices.find(each => each.platform_id === tsgCDL.tenant_id)) {
      // TSG has CDL, need to find if there's a panorama has the same platform_id
      panoramaDevices = _panoramaDevices.filter(each => each.platform_id === tsgCDL.tenant_id)
    }
    else {
      panoramaDevices = _panoramaDevices.filter(each => !each.platform_id)
    }
    setAvailablePanorama(panoramaDevices)

  }, [auth?.user?.notonorafter, auth?.user?.supportAccountIds, dispatch, panoramaDeviceMap, selectedTSGInstances])

  useEffect(() => {
    if (getValues('tsg')?.id && getValues('support_account')?.id) {
      let items = []
      if (selectedTSGInstances.has(APP_ID_PRISMA_ACCESS_PANORAMA)) {
        // amend case
        const panoramaInst = selectedTSGInstances.get(APP_ID_PRISMA_ACCESS_PANORAMA)
        items = [{
          value: panoramaInst.serial_number,
          id: panoramaInst.serial_number,
        }]
      }
      else if (lockTSGPanorama && availablePanoramaDevices[0]) {
        // already has panorama associated from DAS to current TSG
        items = [{
          value: availablePanoramaDevices[0].serial_number,
          id: availablePanoramaDevices[0].serial_number,
        }]
      }
      else {
        items = availablePanoramaDevices.map(each => ({
          value: each.serial_number,
          id: each.serial_number,
        }))
        const createNewOption = { value: CREATE_NEW }
        if (ARRAY.hasArray(panoramaLicenses)) {
          createNewOption.id = panoramaLicenses[0].serial_number
        }
        items.unshift(createNewOption)
      }
      setPanoramaItems(items)
      setSelectedPanorama(items[0])
      setValue('panorama', items[0])
    }
  }, [getValues, availablePanoramaDevices, panoramaLicenses, selectedTSGInstances, setPanoramaItems, trigger, setValue, lockTSGPanorama])

  useEffect(() => {
    if (isCloudManaged) {
      return
    }
    (async () => {
      const tsg_id = getValues('tsg')?.id
      const support_account_id = getValues('support_account')?.id
      if (tsg_id && support_account_id) {
        setPanoramaLoading(true)
        try {
          if (tsg_id === ADD_NEW) {
            setLockTSGPanorama(false)
            await loadPanorama(tsg_id, support_account_id)
          }
          else {
            const devices = await LoadDevices(tsg_id, dispatch)
            const tsgPanoramaDevice = devices.filter(each => each.device_type === APP_ID_PANORAMA)[0]
            if (tsgPanoramaDevice && currentValuesRef.current.tsg_id === tsg_id) {
              setAvailablePanorama([tsgPanoramaDevice])
              setLockTSGPanorama(true)
            }
            else {
              // didn't find panorama bind with selected TSG,
              // make call to get devices per csp, and all the panorama there cannot have any TSG attached
              setLockTSGPanorama(false)
              await loadPanorama(tsg_id, support_account_id)
            }
          }
        }
        catch (error) {
          _setError(error.message)
        }
        setPanoramaLoading(false)
      }
    })()
  }, [watchSupportAccount, watchTsg, isCloudManaged, getValues, dispatch, loadPanorama, _setError])

  useEffect(() => {
    if (watchTsg?.id && watchSupportAccount?.id) {
      setDropdownLock(false)
      if (panoramaItems.length === 1) {
        setDropdownLock(true)
      }
    }
    else {
      setDropdownLock(true)
    }
  }, [panoramaItems, watchSupportAccount, watchTsg])

  useEffect(() => {
    reset({}, { keepValues: true })
  }, [isCloudManaged, reset])

  useEffect(() => {
    setValue('panorama_sn', serialNumber)
    trigger('panorama_sn')
  }, [serialNumber, setError, setValue, trigger])

  useEffect(() => {
    setSerialNumber(selectedPanorama.id)
    if (selectedPanorama.value !== CREATE_NEW) {
      setSNLock(true)
    }
    else {
      setSNLock(false)
    }
  }, [selectedPanorama])

  const validatePanorama = useCallback((target) => {
    if (isCloudManaged) {
      return true
    }
    if (target?.value === 'Create New' || target?.value) {
      clearErrors(['panorama'])
      return true
    }
    return false
  }, [clearErrors, isCloudManaged])

  return (
    <div className="tw-pt-3 tw-pb-3">
      <div className="tw-flex tw-flex-col tw-gap-2 tw-pb-3">
        <Title level={2} size="sm">To set up the Panorama virtual appliance:</Title>
        <Body size="lg">1. Review the <Link target="_blank"
          href="https://docs.paloaltonetworks.com/panorama/9-0/panorama-admin/set-up-panorama/set-up-the-panorama-virtual-appliance/setup-prerequisites-for-the-panorama-virtual-appliance.html">prerequisites.</Link></Body>
        <Body size="lg">2. <Link target="_blank"
          href="https://docs.paloaltonetworks.com/panorama/9-0/panorama-admin/set-up-panorama/set-up-the-panorama-virtual-appliance/install-the-panorama-virtual-appliance.html">Install</Link> Panorama.</Body>
        <Body size="lg">3. Perform <Link target="_blank"
          href="https://docs.paloaltonetworks.com/panorama/9-0/panorama-admin/set-up-panorama/set-up-the-panorama-virtual-appliance/perform-initial-configuration-of-the-panorama-virtual-appliance.html">initial
          configuration.</Link></Body>
        <Body size="lg">4. Upgrade your Panorama. Refer to the <Link target="_blank"
          href="https://docs.paloaltonetworks.com/compatibility-matrix/panorama/plugins.html#id17C8A0XB0V8">compatibility
          matrix</Link> for the minimum Panorama version.x</Body>
      </div>
      <div className="tw-flex tw-flex-col tw-gap-2">
        <Title level={2} size="sm">To set up a Panorama hardware appliance:</Title>
        <Body size="lg">1. Complete steps <Link target="_blank"
          href="https://docs.paloaltonetworks.com/panorama/9-0/panorama-admin/set-up-panorama/set-up-the-m-series-appliance/m-series-setup-overview/set-up-an-m-series-appliance-in-panorama-mode.html">1
          through 6</Link> to set up the Panorama hardware appliance.</Body>
        <Body size="lg">2. Refer to the <Link target="_blank"
          href="https://docs.paloaltonetworks.com/compatibility-matrix/panorama/plugins.html#id17C8A0XB0V8">compatibility
          matrix</Link> for the minimum Panorama version.</Body>
      </div>
      <div className="tw-flex tw-pt-3 tw-gap-2">
        <Details className="tw-flex-1">
          <div className="tw-w-max" style={{ minWidth: '50%' }}>
            <Body size="lg" addClassName="tw-font-lato tw-italic">{LABEL_PANORAMA}</Body>
            <Select
              addClassName='cmm-modal-select'
              disabled={loading || isDropdownLocked}
              name={'panorama'}
              label={LABEL_PANORAMA}
              showLabel={false}
              fitToItemWidth
              items={panoramaItems}
              selectedItem={selectedPanorama}
              isLoading={panoramaLoading}
              register={{
                required: !isCloudManaged,
                onChange: event => {
                  setSelectedPanorama(event.target.value)
                },
                validate: value => validatePanorama(value)

              }}
              placeholder={SELECT_PANORAMA}
            />
          </div>
        </Details>
        <Details className="tw-flex-grow" style={{ flex: 2 }}>
          <div className="tw-w-max">
            <Body size="lg" addClassName="tw-font-lato tw-italic">Panorama Serial Number</Body>
            <div className='tw-flex tw-gap-2'>
              <Input
                name={'panorama_sn'}
                label={'Panorama Serial Number'}
                placeholder={'Serial Number'}
                showLabel={false}
                disabled={loading || isSNLocked}
                register={{
                  required: false,
                  validate: {
                    isRequired: value => isCloudManaged || !!value || 'Serial Number is required',
                    passesRegex: value => isCloudManaged || /^[a-zA-Z0-9-]+$/.test(value) || ERROR_PANORAMA_INVALID
                  }
                }}
              />
            </div>
          </div>
        </Details>
      </div>
    </div>
  )
}

export default PanoramaCard
